import { FC, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TourOverlayProps {
  targetSelector: string;
  isVisible: boolean;
  padding?: number;
  borderRadius?: number;
  children: React.ReactNode;
}

export const TourOverlay: FC<TourOverlayProps> = ({
  targetSelector,
  isVisible,
  padding = 8,
  borderRadius = 8,
  children,
}) => {
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);

  const updateTargetRect = useCallback(() => {
    const element = document.querySelector(targetSelector);
    if (element) {
      const rect = element.getBoundingClientRect();
      setTargetRect({
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      });
    }
  }, [targetSelector, padding]);

  useEffect(() => {
    if (!isVisible) {
      setTargetRect(null);
      return;
    }

    updateTargetRect();

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const element = document.querySelector(targetSelector);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      timeoutId = setTimeout(updateTargetRect, 300);
    }

    window.addEventListener("resize", updateTargetRect);
    window.addEventListener("scroll", updateTargetRect, true);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener("resize", updateTargetRect);
      window.removeEventListener("scroll", updateTargetRect, true);
    };
  }, [isVisible, targetSelector, updateTargetRect]);

  if (!isVisible || !targetRect) {
    return null;
  }

  const overlayContent = (
    <div
      className="fixed inset-0 z-[9998]"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        clipPath: `polygon(
          0% 0%,
          0% 100%,
          ${targetRect.left}px 100%,
          ${targetRect.left}px ${targetRect.top}px,
          ${targetRect.left + targetRect.width}px ${targetRect.top}px,
          ${targetRect.left + targetRect.width}px ${targetRect.top + targetRect.height}px,
          ${targetRect.left}px ${targetRect.top + targetRect.height}px,
          ${targetRect.left}px 100%,
          100% 100%,
          100% 0%
        )`,
      }}
      aria-hidden="true"
    />
  );

  const spotlightBorder = (
    <div
      className="fixed z-[9998] pointer-events-none"
      style={{
        top: targetRect.top - 4,
        left: targetRect.left - 4,
        width: targetRect.width,
        height: targetRect.height,
        borderRadius: borderRadius,
        border: "4px solid rgba(59, 130, 246, 0.5)",
        boxSizing: "content-box",
      }}
      aria-hidden="true"
    />
  );

  return createPortal(
    <>
      {overlayContent}
      {spotlightBorder}
      {children}
    </>,
    document.body
  );
};
